import { z } from 'zod';

/**
 * O que uma tarefa deve produzir ("entregáveis"). Vem da obrigação do catálogo, do modelo de tarefa, da etapa de
 * processo ou do ajuste por cliente no vínculo; a tarefa guarda o retrato na criação e pode ajustar.
 *
 * - fixos: sempre existem (DAS → guia DAS; PIS/COFINS → DARF 8109 + DARF 2172; eSocial → protocolo).
 * - variáveis: dependem do movimento do mês (DCTFWeb e EFD-Reinf: folha, serviços tomados, retenções, importações…).
 *   O catálogo dá o menu de receitas conhecidas; a tarefa aceita quantas guias vierem, cada uma pelo código de receita.
 */
export const PapelDocumento = z.enum(['GUIA', 'RECIBO', 'DOCUMENTO']);
export type PapelDocumento = z.infer<typeof PapelDocumento>;

export const TipoGuiaEntrega = z.enum(['DAS', 'DARF', 'FGTS', 'GPS', 'DAE', 'ISS', 'ICMS', 'GNRE', 'BOLETO', 'OUTRA']);
export type TipoGuiaEntrega = z.infer<typeof TipoGuiaEntrega>;

export const Entregavel = z.object({
  /** estável dentro da tarefa: 'DAS', 'PIS', 'COFINS', 'RECIBO', 'HOLERITES'… */
  chave: z.string().trim().min(1).max(40),
  papel: PapelDocumento,
  /** nome como o cliente vê ("DARF 8109 — PIS faturamento", "RECIBO DE ENTREGA DCTFWEB") */
  nome: z.string().trim().min(1).max(120),
  tipoGuia: TipoGuiaEntrega.nullable().default(null),
  /** códigos de receita aceitos nesta vaga (qualquer um deles preenche) */
  codigosReceita: z.array(z.string().trim().min(4).max(10)).default([]),
  /** obrigatório para concluir a tarefa (respeitando "exigir entrega para concluir") */
  obrigatorio: z.boolean().default(true),
});
export type Entregavel = z.infer<typeof Entregavel>;

export const EntregaveisVariaveis = z.object({
  tipoGuia: TipoGuiaEntrega,
  /** códigos de receita conhecidos para esta obrigação (menu; não é lista fechada) */
  receitas: z.array(z.string().trim().min(4).max(10)).default([]),
  /** como chamar o grupo na tela: "DARFs da DCTFWeb" */
  rotulo: z.string().trim().min(1).max(80),
});
export type EntregaveisVariaveis = z.infer<typeof EntregaveisVariaveis>;

export const Entregaveis = z.object({
  fixos: z.array(Entregavel).default([]),
  variaveis: EntregaveisVariaveis.nullable().default(null),
}).superRefine((v, ctx) => {
  // a chave identifica a vaga dentro da tarefa: repetida, duas vagas receberiam a mesma guia
  const vistas = new Set<string>();
  v.fixos.forEach((f, i) => { if (vistas.has(f.chave)) ctx.addIssue({ code: 'custom', path: ['fixos', i, 'chave'], message: `chave repetida: ${f.chave}` }); vistas.add(f.chave); });
});
export type Entregaveis = z.infer<typeof Entregaveis>;

export const ENTREGAVEIS_VAZIO: Entregaveis = { fixos: [], variaveis: null };

/** Lê o JSON gravado no banco com tolerância (nulo, vazio ou formato antigo → vazio). */
export function lerEntregaveis(v: unknown): Entregaveis {
  const r = Entregaveis.safeParse(v ?? {});
  return r.success ? r.data : ENTREGAVEIS_VAZIO;
}
